import React, { useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../redux/slice";
import {
  selectMyTicketsFilters,
  selectTicketsPagination,
  selectProjectDetails,
} from "../../redux/selector";
import ProjectFilter from "./ProjectFilter";
import StatusFilter from "./StatusFilter";
import DueDateFilter from "./DueDateFilter";
import { toTitleCase } from "../../helpers/Index";

const TicketFilters = () => {
  const dispatch = useDispatch();
  const selectedFilters = useSelector(selectMyTicketsFilters);
  const pagination = useSelector(selectTicketsPagination);
  const projectDetails = useSelector(selectProjectDetails);
  const hasInitializedProject = useRef(false);
  const hasInitializedStatus = useRef(false);

  const projectOptions = useMemo(
    () =>
      projectDetails.map((p) => ({
        id: p._id,
        value: p.name,
      })),
    [projectDetails],
  );

  const statusOptions = useMemo(() => {
    const map = new Map<string, { id: string; value: string }>();
    if (
      !selectedFilters.projectIds ||
      selectedFilters.projectIds.length === 0
    ) {
      return [];
    }
    const selectedProjects = projectDetails.filter((p) =>
      selectedFilters.projectIds.includes(p._id),
    );
    selectedProjects.forEach((p) => {
      p.projectStatuses?.forEach((s) => {
        if (!map.has(s._id)) {
          map.set(s._id, { id: s._id, value: toTitleCase(s.name) });
        }
      });
    });
    return Array.from(map.values());
  }, [projectDetails, selectedFilters.projectIds]);

  const handleFilterChange = (
    key: keyof typeof selectedFilters,
    values: string[],
  ) => {
    dispatch(
      actions.setMyTicketsFilters({ ...selectedFilters, [key]: values }),
    );
    dispatch(actions.setTicketsPagination({ ...pagination, page: 1 }));
    dispatch(actions.getMyTickets());
  };

  const handleProjectFilterChange = (values: string[]) => {
    dispatch(
      actions.setMyTicketsFilters({
        ...selectedFilters,
        projectIds: values,
        statusIds: [],
      }),
    );
    hasInitializedStatus.current = false;
    dispatch(actions.setTicketsPagination({ ...pagination, page: 1 }));
    dispatch(actions.getMyTickets());
  };
  const prevOptionsLength = useRef(0);
  const prevSelectedLength = useRef(0);

  useEffect(() => {
    if (!hasInitializedProject.current) {
      if (selectedFilters.projectIds?.length > 0) {
        hasInitializedProject.current = true;
      } else if (projectOptions.length > 0) {
        dispatch(
          actions.setMyTicketsFilters({
            ...selectedFilters,
            projectIds: projectOptions.map((o) => o.id),
          }),
        );
        hasInitializedProject.current = true;
        dispatch(actions.setTicketsPagination({ ...pagination, page: 1 }));
        dispatch(actions.getMyTickets());
      }
    } else {
      if (
        projectOptions.length > prevOptionsLength.current &&
        prevOptionsLength.current > 0 &&
        prevSelectedLength.current === prevOptionsLength.current
      ) {
        dispatch(
          actions.setMyTicketsFilters({
            ...selectedFilters,
            projectIds: projectOptions.map((o) => o.id),
          }),
        );
      }
    }

    prevOptionsLength.current = projectOptions.length;
    prevSelectedLength.current = selectedFilters.projectIds?.length || 0;
  }, [dispatch, projectOptions, selectedFilters]);

  useEffect(() => {
    if (hasInitializedStatus.current) return;
    if (selectedFilters.statusIds?.length > 0) {
      hasInitializedStatus.current = true;
      return;
    }
    if (statusOptions.length > 0) {
      dispatch(
        actions.setMyTicketsFilters({
          ...selectedFilters,
          statusIds: statusOptions.map((o) => o.id),
        }),
      );
      hasInitializedStatus.current = true;
      dispatch(actions.setTicketsPagination({ ...pagination, page: 1 }));
      dispatch(actions.getMyTickets());
    }
  }, [dispatch, statusOptions.length, selectedFilters.statusIds?.length]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ProjectFilter
        selectedIds={selectedFilters.projectIds || []}
        onUpdate={handleProjectFilterChange}
        options={projectOptions}
      />
      <DueDateFilter
        selectedIds={selectedFilters.dueDate || []}
        onUpdate={(vals) => handleFilterChange("dueDate", vals)}
        options={[]}
      />
      <StatusFilter
        selectedIds={selectedFilters.statusIds || []}
        onUpdate={(vals) => handleFilterChange("statusIds", vals)}
        options={statusOptions}
        disabled={
          !selectedFilters.projectIds || selectedFilters.projectIds.length === 0
        }
      />
    </div>
  );
};

export default TicketFilters;
